import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Star, Trash2, Upload } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import MediaLibraryPicker from "@/components/admin/MediaLibraryPicker";
import AiTextButton from "@/components/admin/AiTextButton";
import { useSeo } from "@/lib/seo";
import { fcfa } from "@/lib/format";

const CATEGORIES = ["Robinets", "Mitigeurs", "Siphons", "Meubles de douche", "Colonnes de douche", "Baignoires", "Accessoires", "Lits coffres"];

const inputClass =
  "h-11 w-full rounded-md border border-clay bg-white px-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

const AdminProductEdit = () => {
  useSeo({ title: "Modifier un produit", noindex: true });
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [catList, setCatList] = useState(CATEGORIES);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Photo ajoutée à la galerie — pensez à enregistrer");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) =>
    setForm((f) => {
      if (f.images.length <= 1) {
        toast.error("Un produit doit garder au moins une photo");
        return f;
      }
      return { ...f, images: f.images.filter((_, i) => i !== idx) };
    });

  const setPrimary = (idx) =>
    setForm((f) => {
      const imgs = [...f.images];
      const [img] = imgs.splice(idx, 1);
      return { ...f, images: [img, ...imgs] };
    });

  useEffect(() => {
    api
      .get("/auth/me")
      .then(() => {
        api.get("/categories").then((r) => setCatList(r.data.map((c) => c.name))).catch(() => {});
        return api.get(`/products/${id}`);
      })
      .then((r) =>
        setForm({
          name: r.data.name,
          category: r.data.category,
          price: String(r.data.price),
          delivery_estimate: r.data.delivery_estimate || "",
          shipping_fee: String(r.data.shipping_fee ?? ""),
          images: r.data.images?.length ? r.data.images : [r.data.image],
          description: r.data.description || "",
          badge: r.data.badge || "",
          features: (r.data.features || []).join("\n"),
        })
      )
      .catch((err) => {
        if (err.response?.status === 401) navigate("/admin/login");
        else toast.error(formatApiErrorDetail(err.response?.data?.detail));
      });
  }, [id, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/products/${id}`, {
        name: form.name,
        category: form.category,
        price: parseInt(form.price, 10),
        delivery_estimate: form.delivery_estimate,
        shipping_fee: parseInt(form.shipping_fee || "0", 10),
        image: form.images[0],
        images: form.images,
        description: form.description,
        badge: form.badge || null,
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Produit enregistré");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sand">
        <Loader2 className="h-8 w-8 animate-spin text-terracotta" />
      </div>
    );
  }

  return (
    <div data-testid="admin-product-edit-page" className="min-h-screen bg-sand">
      <header className="border-b border-clay bg-cream">
        <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-6 lg:px-10">
          <Link to="/" className="font-display text-2xl font-bold text-bark">
            Sama-<span className="italic text-terracotta">Keur</span>
          </Link>
          <Link to="/admin" state={{ tab: "products" }} data-testid="admin-edit-back-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-bark/50 transition-colors hover:text-terracotta">
            <ArrowLeft className="h-4 w-4" /> Tableau de bord
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12 lg:px-10">
        <h1 className="font-display text-3xl font-semibold text-bark">Modifier le produit</h1>
        <p className="mt-1 text-sm text-bark/50">Les modifications sont visibles immédiatement sur la boutique.</p>

        <form onSubmit={save} className="mt-10 grid gap-12 lg:grid-cols-2">
          <div>
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">
              Galerie photos ({form.images.length})
            </p>
            <div className="relative">
              <div className="absolute -left-3 -top-3 h-full w-full border border-terracotta/30" />
              <div className="relative aspect-[4/5] overflow-hidden border border-clay bg-cream">
                <img
                  data-testid="admin-edit-image-preview"
                  src={form.images[0]}
                  alt={form.name}
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
                  onLoad={(e) => { e.currentTarget.style.opacity = "1"; }}
                />
                <span className="absolute left-4 top-4 bg-terracotta px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-sand">
                  Photo principale
                </span>
              </div>
            </div>

            <div data-testid="admin-edit-gallery" className="mt-6 grid grid-cols-4 gap-3">
              {form.images.map((img, idx) => (
                <div key={img + idx} data-testid={`admin-gallery-item-${idx}`} className={`group relative aspect-square overflow-hidden border ${idx === 0 ? "border-terracotta" : "border-clay"}`}>
                  <img src={img} alt={`${form.name} ${idx + 1}`} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-bark/0 opacity-0 transition-all duration-300 group-hover:bg-bark/50 group-hover:opacity-100">
                    {idx !== 0 && (
                      <button
                        type="button"
                        data-testid={`admin-gallery-primary-${idx}`}
                        onClick={() => setPrimary(idx)}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-sand text-bark hover:bg-ochre"
                        title="Définir comme photo principale"
                      >
                        <Star className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {form.images.length > 1 && (
                      <button
                        type="button"
                        data-testid={`admin-gallery-remove-${idx}`}
                        onClick={() => removeImage(idx)}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-sand text-bark transition-colors hover:bg-destructive hover:text-sand"
                        title="Retirer cette photo"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              data-testid="admin-edit-file-input"
              onChange={onFile}
            />
            <button
              type="button"
              data-testid="admin-edit-upload-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-bark text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors duration-300 hover:bg-terracotta disabled:opacity-50"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Envoi en cours…" : "Ajouter une photo"}
            </button>
            <div className="mt-3">
              <MediaLibraryPicker
                testid="admin-edit-media-library"
                onSelect={(url) =>
                  setForm((f) => {
                    if (f.images.includes(url)) {
                      toast.error("Cette photo est déjà dans la galerie");
                      return f;
                    }
                    toast.success("Photo ajoutée à la galerie — pensez à enregistrer");
                    return { ...f, images: [...f.images, url] };
                  })
                }
              />
            </div>
            <p className="mt-2 text-xs text-bark/40">
              JPG, PNG ou WebP. Survolez une miniature pour la définir comme principale ou la retirer.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Nom</label>
                <AiTextButton
                  kind="product_name"
                  testid="admin-edit-name-ai-button"
                  context={() => `${form.category}${form.description ? " — " + form.description : ""}`}
                  onGenerated={(t) => setForm((f) => ({ ...f, name: t }))}
                />
              </div>
              <input data-testid="admin-edit-name-input" required value={form.name} onChange={set("name")} className={inputClass} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Catégorie</label>
                <select
                  data-testid="admin-edit-category-select"
                  value={form.category}
                  onChange={set("category")}
                  className={inputClass}
                >
                  {catList.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Prix (FCFA)</label>
                <input data-testid="admin-edit-price-input" required type="number" min="0" value={form.price} onChange={set("price")} className={inputClass} />
                <p data-testid="admin-edit-price-preview" className="mt-1 text-xs text-bark/40">{fcfa(parseInt(form.price || "0", 10))}</p>
              </div>
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Badge (facultatif)</label>
              <input data-testid="admin-edit-badge-input" placeholder="Ex : Nouveau, Best-seller" value={form.badge} onChange={set("badge")} className={inputClass} />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Délai de livraison estimé</label>
              <input
                data-testid="admin-edit-delivery-estimate-input"
                placeholder="Ex : 2 à 3 jours ouvrés"
                value={form.delivery_estimate}
                onChange={set("delivery_estimate")}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-bark/40">Affiché juste sous le prix sur la fiche produit. Laissez vide pour masquer.</p>
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Frais de livraison depuis la France (FCFA)</label>
              <input
                data-testid="admin-edit-shipping-fee-input"
                type="number"
                min="0"
                placeholder="Ex : 4500"
                value={form.shipping_fee}
                onChange={set("shipping_fee")}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-bark/40">Ajouté au prix du produit sur la fiche et dans le panier. 0 pour ne rien facturer.</p>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between gap-3">
                <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Description complète</label>
                <AiTextButton
                  kind="product_description"
                  testid="admin-edit-description-ai-button"
                  context={() => `${form.name} — ${form.category} — ${form.price} FCFA${form.description ? " — texte actuel : " + form.description : ""}`}
                  onGenerated={(t) => setForm((f) => ({ ...f, description: t }))}
                />
              </div>
              <textarea
                data-testid="admin-edit-description-input"
                value={form.description}
                onChange={set("description")}
                className="min-h-[120px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Caractéristiques (une par ligne)</label>
              <textarea
                data-testid="admin-edit-features-input"
                value={form.features}
                onChange={set("features")}
                className="min-h-[100px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                data-testid="admin-edit-save-button"
                className="flex h-12 items-center gap-2 rounded-full bg-terracotta px-8 text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Enregistrer
              </button>
              <Link
                to="/admin"
                state={{ tab: "products" }}
                data-testid="admin-edit-cancel-link"
                className="flex h-12 items-center rounded-full border border-clay px-6 text-xs font-bold uppercase tracking-[0.15em] text-bark/60 transition-colors hover:border-terracotta hover:text-terracotta"
              >
                Annuler
              </Link>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminProductEdit;
